/** Renderer-neutral, bounded leases. In-flight work keeps its reservation even
 * after cancellation until the producer settles; a late producer never publishes. */
export function createRenderResourceCache({ maxBytes = 64 * 1024 * 1024, maxEntries = 256,
  maxPending = 8, maxWaiters = 1024, timeoutMs = 30000, onError = () => undefined } = {}) {
  for (const value of [maxBytes, maxEntries, maxPending, maxWaiters, timeoutMs]) {
    if (!Number.isSafeInteger(value) || value < 1) throw new RangeError('Invalid resource budget');
  }
  const entries = new Map(), owned = new Set();
  let disposed = false, clock = 0, bytes = 0, pending = 0, waiters = 0;
  let created = 0, destroyed = 0, hits = 0, evictions = 0, cleanupErrors = 0;
  const failure = code => Object.assign(new Error(code), { code });
  const report = error => { cleanupErrors++; try { onError(error); } catch { /* Diagnostics cannot interrupt cleanup. */ } };
  const destroy = resource => {
    if (!resource) return;
    try { resource.dispose(); } catch (error) { report(error); }
    destroyed++;
  };
  const remove = entry => {
    if (entry.removed) return;
    entry.removed = true;
    if (entries.get(entry.key) === entry) entries.delete(entry.key);
    owned.delete(entry); bytes -= entry.bytes; destroy(entry.resource); entry.resource = null;
  };
  const lease = entry => {
    entry.refs++; entry.used = ++clock; let released = false;
    return Object.freeze({
      get value() { if (released || entry.removed) throw failure('RESOURCE_LEASE_RELEASED'); return entry.resource.value; },
      release() {
        if (released) return;
        released = true; entry.refs--;
        if (entry.retired && entry.refs === 0 && !entry.loading) remove(entry);
      }
    });
  };
  const retire = (entry, reason) => {
    if (entry.retired) return;
    entry.retired = true;
    if (entries.get(entry.key) === entry) entries.delete(entry.key);
    entry.controller.abort(reason);
    for (const waiter of [...entry.waiters]) waiter.finish(reason);
    if (!entry.loading && entry.refs === 0) remove(entry);
  };
  const reserve = amount => {
    while (true) {
      if (bytes + amount <= maxBytes && owned.size < maxEntries) return;
      const candidate = [...owned].filter(e => !e.loading && e.refs === 0).sort((a, b) => a.used - b.used)[0];
      if (!candidate) throw failure('RESOURCE_BUDGET_EXCEEDED');
      evictions++; remove(candidate);
    }
  };
  const subscribe = (entry, signal) => new Promise((resolve, reject) => {
    const waiter = { finish(error) {
      if (!entry.waiters.delete(waiter)) return;
      waiters--; signal?.removeEventListener('abort', abort);
      if (error) reject(error); else resolve(lease(entry));
    } };
    function abort() {
      waiter.finish(failure('RESOURCE_ABORTED'));
      if (entry.loading && entry.waiters.size === 0) retire(entry, failure('RESOURCE_ABORTED'));
    }
    entry.waiters.add(waiter); waiters++;
    signal?.addEventListener('abort', abort, { once: true });
    if (signal?.aborted) abort();
  });
  const start = (entry, create) => {
    const timer = setTimeout(() => retire(entry, failure('RESOURCE_TIMEOUT')), timeoutMs);
    Promise.resolve().then(() => {
      if (entry.retired || disposed) throw failure('RESOURCE_ABORTED');
      return create({ signal: entry.controller.signal });
    }).then(resource => {
      if (!resource || typeof resource.dispose !== 'function' || !Number.isSafeInteger(resource.bytes)
        || resource.bytes < 0 || resource.bytes > entry.bytes || !Object.hasOwn(resource, 'value')) {
        if (resource && typeof resource.dispose === 'function') destroy(resource);
        throw failure('RESOURCE_INVALID_RESULT');
      }
      created++;
      if (entry.retired || disposed) { destroy(resource); return; }
      bytes += resource.bytes - entry.bytes; entry.bytes = resource.bytes; entry.resource = resource;
      for (const waiter of [...entry.waiters]) waiter.finish();
    }).catch(error => { retire(entry, error); }).finally(() => {
      clearTimeout(timer); pending--; entry.loading = false;
      if (entry.retired || !entry.resource) { if (entry.refs === 0) remove(entry); }
    });
  };
  return Object.freeze({
    acquire(key, { bytes: reservation, create, signal } = {}) {
      if (disposed) return Promise.reject(failure('RESOURCE_CACHE_DISPOSED'));
      if (typeof key !== 'string' || !key || key.length > 8192 || !Number.isSafeInteger(reservation)
        || reservation < 1 || reservation > maxBytes || typeof create !== 'function') return Promise.reject(failure('RESOURCE_INVALID_REQUEST'));
      if (signal?.aborted) return Promise.reject(failure('RESOURCE_ABORTED'));
      const existing = entries.get(key);
      if (existing) {
        if (existing.reservation !== reservation) return Promise.reject(failure('RESOURCE_RESERVATION_MISMATCH'));
        hits++;
        if (existing.resource) return Promise.resolve(lease(existing));
        if (waiters >= maxWaiters) return Promise.reject(failure('RESOURCE_WAITER_LIMIT'));
        return subscribe(existing, signal);
      }
      if (pending >= maxPending) return Promise.reject(failure('RESOURCE_PENDING_LIMIT'));
      if (waiters >= maxWaiters) return Promise.reject(failure('RESOURCE_WAITER_LIMIT'));
      try { reserve(reservation); } catch (error) { return Promise.reject(error); }
      const entry = { key, bytes: reservation, reservation, refs: 0, used: ++clock,
        loading: true, retired: false, removed: false, resource: null, waiters: new Set(), controller: new AbortController() };
      entries.set(key, entry); owned.add(entry); bytes += reservation; pending++;
      const promise = subscribe(entry, signal); start(entry, create); return promise;
    },
    invalidate(key) { const entry = entries.get(key); if (entry) retire(entry, failure('RESOURCE_INVALIDATED')); },
    clear() { for (const entry of [...entries.values()]) retire(entry, failure('RESOURCE_INVALIDATED')); },
    dispose() {
      if (disposed) return;
      disposed = true;
      for (const entry of [...owned]) retire(entry, failure('RESOURCE_CACHE_DISPOSED'));
    },
    snapshot: () => ({ disposed, bytes, entries: owned.size, pending, waiters,
      leases: [...owned].reduce((sum, e) => sum + e.refs, 0), created, destroyed, hits, evictions, cleanupErrors,
      maxBytes, maxEntries, maxPending, maxWaiters })
  });
}
